import { AbstractCookie } from "./AbstractCookie";
import { ABTestLPDataConstants } from "./ABTestLPDataConstants";
import { SessionTypes } from "../constants/SessionTypes";
import { StringUtils } from "../stringhelper/StringUtils";

import { CDN } from "@goodgamestudios/world-assignment";

export class AccountCookie extends AbstractCookie {
    public static NAME: string = "GGSAccount";

    public static ATTRIBUTE_ACCOUNT_ID: string = "accountId";
    public static ATTRIBUTE_AGE_GATE_DATA: string = "agd";
    public static ATTRIBUTE_CDN: string = "cdn";
    public static ATTRIBUTE_SESSION_TYPE: string = "sessionType";
    public static ATTRIBUTE_SESSION_COUNT: string = "sessionCount";

    public static ATTRIBUTES: string[] = [
        AccountCookie.ATTRIBUTE_ACCOUNT_ID,
        AccountCookie.ATTRIBUTE_AGE_GATE_DATA,
        AccountCookie.ATTRIBUTE_CDN,
        AccountCookie.ATTRIBUTE_SESSION_TYPE,
        AccountCookie.ATTRIBUTE_SESSION_COUNT
    ];

    private _accountId: string = null;
    private _isNewAccount: boolean = false;
    private _journeyHash: string;
    private _sessionType: number;
    private _sessionCount: number;

    /**
     * Constructor of account cookie.
     */
    constructor(journeyHash: string) {
        super("ac");
        this._journeyHash = journeyHash;
    }

    /**
     * Read the current cookie or create a new one if the cookie was just created.
     */
    protected readLocalStorage(): void {
        // if there is no account id, generate one!
        if (!this.hasAttribute(AccountCookie.ATTRIBUTE_ACCOUNT_ID)) {
            this._isNewAccount = true;

            // check if a valid journey hash is provided, otherwise generate a random account id
            if (this.stringIsValid(this._journeyHash)) {
                // use journeyHash Function from external interface
                this._accountId = this._journeyHash;
            } else {
                // generate a new random account id
                this._accountId = Date.now().toString() + (Math.random() * 999999).toFixed();
            }

            this.setAttribute(AccountCookie.ATTRIBUTE_ACCOUNT_ID, this._accountId, false);
        } else {
            // read account id
            this._accountId = this.getAttributeAsString(AccountCookie.ATTRIBUTE_ACCOUNT_ID);

            // check if a valid journey hash is provided
            if (this.stringIsValid(this._journeyHash) && this._accountId !== this._journeyHash) {
                // use journey hash instead of old account id
                this._accountId = this._journeyHash;
                this.setAttribute(AccountCookie.ATTRIBUTE_ACCOUNT_ID, this._accountId, false);
            }
        }

        // read account id
        this._accountId = this.getAttributeAsString(AccountCookie.ATTRIBUTE_ACCOUNT_ID);

        // persist changes
        this.writeLocalStorage();
    }

    private stringIsValid(value: string): boolean {
        return value !== null && value !== undefined && value.length > 0;
    }

    /**
     * Increase session count.
     */
    public newSession(versionNumber: string, versionNumberFromCookie: string): void {
        // initialize session count for new account
        if (this._isNewAccount) {
            this._sessionType = SessionTypes.FIRST_SESSION;
            this._sessionCount = 1;
        } else {
            // user account is not new, it should be a regular session
            this._sessionType = SessionTypes.REGULAR_SESSION;

            // check if version number was previously added into cookie
            if (versionNumberFromCookie !== null && versionNumberFromCookie !== undefined) {
                // check if version changed
                if (versionNumberFromCookie !== versionNumber) {
                    // First session after update
                    this._sessionType = SessionTypes.FIRST_SESSION_AFTER_UPDATE;
                }
            }

            // read session count attribute
            if (this.hasAttribute(AccountCookie.ATTRIBUTE_SESSION_COUNT)) {
                this._sessionCount = this.getAttributeAsInt(AccountCookie.ATTRIBUTE_SESSION_COUNT) + 1;
            } else {
                this._sessionCount = 2;
            }
        }

        // store information about current session
        this.setAttribute(AccountCookie.ATTRIBUTE_SESSION_TYPE, this._sessionType, false);
        this.setAttribute(AccountCookie.ATTRIBUTE_SESSION_COUNT, this._sessionCount, false);

        // persist changes
        this.writeLocalStorage();
    }

    public get accountId(): string {
        return this._accountId;
    }

    public get isNewAccount(): boolean {
        return this._isNewAccount;
    }

    public get cdn(): CDN {
        if (this.hasAttribute(AccountCookie.ATTRIBUTE_CDN)) {
            return this.getAttributeAsNumber(AccountCookie.ATTRIBUTE_CDN);
        } else {
            return CDN.None;
        }
    }

    public set cdn(value: CDN) {
        this.setAttribute(AccountCookie.ATTRIBUTE_CDN, value);
    }

    public get ageGateData(): string {
        if (this.hasAttribute(AccountCookie.ATTRIBUTE_AGE_GATE_DATA)) {
            return StringUtils.decrypt(this.getAttributeAsString(AccountCookie.ATTRIBUTE_AGE_GATE_DATA));
        } else {
            return null;
        }
    }

    public set ageGateData(value: string) {
        this.setAttribute(AccountCookie.ATTRIBUTE_AGE_GATE_DATA, StringUtils.encrypt(value));
    }

    /**
     * Gets the session type.
     * @return The session type.
     */
    public get sessionType(): number {
        return this.getAttributeAsNumber(AccountCookie.ATTRIBUTE_SESSION_TYPE);
    }

    /**
     * Gets the session count.
     * @return The session count.
     */
    public get sessionCount(): number {
        return this.getAttributeAsNumber(AccountCookie.ATTRIBUTE_SESSION_COUNT);
    }
}
